/** Un pas rond pour les colonnes.
 *
 *  Découper l'étendue en sept parts égales donnerait des bornes du genre
 *  « 109–112,4 », qu'on ne lit pas. On prend donc le premier pas rond assez
 *  large : les colonnes tombent alors sur des dizaines ou des cinq, comme les
 *  panneaux d'un practice. */
function pasRond(etendue: number): number {
    const brut = etendue / 7;
    return [1, 2, 5, 10, 20, 25, 50, 100].find((p) => p >= brut) ?? 200;
}

/* Comment les balles se groupent.
 *
 * ── Pourquoi une fourchette ne suffit pas ───────────────────────────────────
 *
 * « De 109 à 133 » décrit aussi bien vingt balles serrées autour de 125 avec
 * une seule courte, qu'un étalement régulier sur toute la plage. Ce ne sont pas
 * les mêmes coups, ni le même travail à faire : le premier joueur a un club
 * fiable et une balle ratée, le second n'a pas de club. Seule la RÉPARTITION
 * les distingue, et il faut les distances une à une pour la dessiner.
 *
 * ── La colonne de la moyenne se détache ─────────────────────────────────────
 *
 * Elle relie cette figure à la barre du dessus, où la même moyenne est une
 * pastille. Sans ce lien, ce sont deux dessins sans rapport ; avec lui, on voit
 * que la pastille tombe dans la colonne la plus haute — ou qu'elle n'y tombe
 * pas, ce qui est déjà un renseignement.
 */
export function GroupementBalles({ distances, moyenne, unite }: {
    distances: number[];
    moyenne: number;
    unite: string;
}) {
    if (distances.length < 3) return null;

    const bas = Math.min(...distances);
    const haut = Math.max(...distances);
    const pas = pasRond(Math.max(1, haut - bas));
    const debut = Math.floor(bas / pas) * pas;
    const n = Math.max(1, Math.ceil((haut + 0.001 - debut) / pas));

    const seaux = Array.from({ length: n }, () => 0);
    for (const v of distances) {
        const i = Math.min(n - 1, Math.max(0, Math.floor((v - debut) / pas)));
        seaux[i] = (seaux[i] ?? 0) + 1;
    }
    const plus = Math.max(...seaux);
    const seauMoyenne = Math.min(n - 1, Math.max(0, Math.floor((moyenne - debut) / pas)));

    return (
        <figure className="m-0">
            <div className="flex h-20 items-stretch gap-1">
                {seaux.map((v, i) => (
                    <div key={debut + i * pas} className="flex flex-1 flex-col justify-end gap-0.5">
                        <span className="text-center text-[10px] font-bold leading-none tabular-nums opacity-70">
                            {v > 0 ? v : ''}
                        </span>
                        <div className="w-full rounded-t-[3px]"
                            style={{
                                height: `${(v / plus) * 100}%`,
                                minHeight: v > 0 ? 3 : 0,
                                background: i === seauMoyenne
                                    ? 'var(--tuile-encre)'
                                    : 'color-mix(in oklab, var(--tuile-encre) 30%, transparent)',
                            }} />
                    </div>
                ))}
            </div>
            {/* Les deux bouts de l'échelle, et rien entre les deux : un chiffre
                sous chaque colonne se chevaucherait à sept colonnes de large. */}
            <div className="mt-1 flex justify-between text-[10px] opacity-55">
                <span className="font-score tabular-nums">{debut} {unite}</span>
                <span className="font-score tabular-nums">{debut + n * pas} {unite}</span>
            </div>
        </figure>
    );
}
